import { BiCardQuery } from '@euler/model/bi/blend';
import { BiCardView, BiDateFilterUse } from '@euler/model/bi/dashboard';
import { BiVizSpec } from '@euler/model/bi/viz';
import deepEqual from 'deep-equal';
import { BiCardEditor } from '../../dashboard/editor/functions/useBiCardEditor';
import { BiCardDataView } from '../../dashboard/functions/useBiCardData';

/** What the card page draws: the saved card, or the form's live preview. */
export interface BiCardShown {
  title: string;
  spec: BiCardQuery;
  viz: BiVizSpec;
  data: BiCardDataView;
  /** The form differs from the saved card: what is drawn is not saved. */
  unsaved: boolean;
  /**
   * The form's own query run, which the dashboard's date filter does not
   * apply to (the saved card's rows do).
   */
  previewRun: boolean;
}

/**
 * The saved card with its data, unless the form has changed it: a look
 * change redraws the saved rows (dashboard filters and all); a query
 * change shows the form's own preview run.
 */
export function biCardShown(
  card: BiCardView,
  data: BiCardDataView,
  editor: BiCardEditor | null,
): BiCardShown {
  const saved = {
    title: card.title,
    spec: card.spec,
    viz: card.viz,
    data,
    unsaved: false,
    previewRun: false,
  };
  if (!editor?.viz || !editor.spec) return saved;
  const title = editor.state.title.trim() || card.title;
  const sameSpec = deepEqual(editor.spec, card.spec, { strict: true });
  const unsaved =
    !sameSpec ||
    title !== card.title ||
    !deepEqual(editor.viz, card.viz, { strict: true });
  if (sameSpec)
    return {
      title,
      spec: card.spec,
      viz: editor.viz,
      data,
      unsaved,
      previewRun: false,
    };
  const { preview } = editor;
  return {
    title,
    spec: editor.spec,
    viz: editor.viz,
    unsaved,
    previewRun: true,
    data:
      preview.kind === 'ready'
        ? {
            kind: 'ready',
            result: { current: preview.result },
            dateFilter: BiDateFilterUse.None,
          }
        : preview.kind === 'failed'
        ? { kind: 'failed', message: preview.message }
        : { kind: 'loading' },
  };
}
